
import { computed, defineComponent, onBeforeMount, reactive } from 'vue'

// components
import PageTitle from '@/common/components/UI/PageTitle/index.vue'
import FailedFetch from '@/common/components/UI/FailedFetch/index.vue'
import TheButton from '@/common/components/UI/TheButton/index.vue'
import EnterData from '@/modules/WithServicesModule/components/EnterData/index.vue'
import Cheap from '@/common/components/UI/Cheap/index.vue'
import TheTable from '@/common/components/UI/TheTable/index.vue'
import TableScrollWrapper from '@/common/components/UI/TableScrollWrapper/index.vue'

// module components
import WithServicesFilters from '@/modules/InvoicesHistoryModule/components/WithServicesFilters/index.vue'

// realisations
import { invoices } from '@/core/realisations/invoices.realisation'

// types
import {
    CompleteDataWithServicesPayloadT,
    DataWithServicesT,
    InvoicesDownloadExcelResponseT,
    WithServicesListParamsT,
} from '@/core/types/invoices.types'

// classes
import { DataState } from '@/core/classes/DataState.class'

// constants
import { DataWithServicesColumns } from '@/core/constants/columns/DataWithServices.columns'

// composable
import useNotifications from '@/common/composable/useNotifications'
import { AdditionalServicesWithServicesI } from '@/core/types/additional_services.types'
import useAccount from '@/common/composable/useAccount'
import { useFile } from '@/common/composable/useFile'

// constants
import {
    WITH_SERVICES_STATUSES_COLORS,
    NOT_STATUS_IDS,
} from '@/core/constants/with_services.constants'

import { formatDateWithTimeFromBackend } from '@/common/helpers/formatDate'

const MAX_NAME_LENGTH_IN_TABLE = 25

export default defineComponent({
    components: {
        'page-title': PageTitle,
        'failed-fetch': FailedFetch,
        'with-services-filters': WithServicesFilters,
        TheButton,
        EnterData,
        Cheap,
        TheTable,
        TableScrollWrapper,
    },
    setup() {
        const state = reactive(new DataState<DataWithServicesT>({}))
        const notifications = useNotifications()
        const user = useAccount()
        const { saveFileByURL } = useFile()

        const filters_state = reactive({
            limit: 50,
            //
            invoice_number: null as string | null,
            order_id: null as string | null,
            isPickup: 0,
            isDelivery: 0,
            isBillingEnabled: false,
            from: null as number | null,
            to: null as number | null,
            additional_services: [] as number[],
            created_at: null as string | null,
            company_id: null as number | null,
            statusIds: [] as number[],
            takeDateFrom: null as string | null,
            takeDateTo: null as string | null,
            deliveredAtFrom: null as string | null,
            deliveredAtTo: null as string | null,
            additionalServiceStatusId: null as number | null,
        })

        const modals_state = reactive({
            is_loading: false,
            //
            invoice_id: null as number | null,
            invoice_number: null as string | null,
            additional_services: null as AdditionalServicesWithServicesI[] | null,
            is_on_enter_data: false,
        })

        const getPayload = () => {
            const params: WithServicesListParamsT = {
                page: 1,
                limit: filters_state.limit,
                createdAt: filters_state.created_at,
                takeDateFrom: filters_state.takeDateFrom,
                takeDateTo: filters_state.takeDateTo,
                deliveredAtFrom: filters_state.deliveredAtFrom,
                deliveredAtTo: filters_state.deliveredAtTo,
                isBillingEnabled: filters_state.isBillingEnabled ? 1 : null,
                from: filters_state.from,
                to: filters_state.to,
                isPickup: filters_state.isPickup,
                isDelivery: filters_state.isDelivery,
                additional_services: filters_state.additional_services,
                company_id: filters_state.company_id,
                statusIds: filters_state.statusIds,
                notStatusIds: NOT_STATUS_IDS,
                additionalServiceStatusId: filters_state.additionalServiceStatusId,
            }

            if (filters_state.invoice_number) {
                params.invoice_number = filters_state.invoice_number
            }

            if (filters_state.order_id) {
                params.order_id = filters_state.order_id
            }

            return params
        }

        const loadDataWithServices = async (page: number) => {
            state.pagination.current_page = page

            try {
                state.is_loading = true

                const params = getPayload()

                const response = await invoices.withServicesList({ ...params, page })
                state.list = response.data
                state.pagination.total_pages = response.meta.last_page
                state.pagination.total_items = response.meta.total

                state.is_failed = false
                state.failure.error_state.is_cancel = false
            } catch (error) {
                state.failure.catchError(error)

                if (!state.failure.error_state.is_cancel) {
                    state.is_failed = true
                }
            } finally {
                if (!state.failure.error_state.is_cancel) {
                    state.is_loading = false
                }
            }
        }

        onBeforeMount(() => {
            loadDataWithServices(1)
        })

        const cutName = (name: string): string => {
            if (name && name.length > MAX_NAME_LENGTH_IN_TABLE) {
                let name_parts = name.split('')
                name_parts = name_parts.splice(0, MAX_NAME_LENGTH_IN_TABLE - 1)
                name_parts.push('...')
                return name_parts.join('')
            }

            return name
        }

        const applyFilters = (params: {
            additional_services: number[]
            created_at: string | null
            from: number | null
            to: number | null
            invoice_number: string | null
            order_id: string | null
            company_id: number | null
            isPickup: number
            isDelivery: number
            isBillingEnabled: boolean
            statusIds: number[]
            additionalServiceStatusId: number
            takeDateFrom: string | null
            takeDateTo: string | null
            deliveredAtFrom: string | null
            deliveredAtTo: string | null
        }) => {
            filters_state.additional_services = params.additional_services
            filters_state.created_at = params.created_at
            filters_state.from = params.from
            filters_state.to = params.to
            filters_state.invoice_number = params.invoice_number
            filters_state.order_id = params.order_id
            filters_state.company_id = params.company_id
            filters_state.isPickup = params.isPickup
            filters_state.isDelivery = params.isDelivery
            filters_state.isBillingEnabled = params.isBillingEnabled
            filters_state.statusIds = params.statusIds
            filters_state.takeDateFrom = params.takeDateFrom
            filters_state.takeDateTo = params.takeDateTo
            filters_state.deliveredAtFrom = params.deliveredAtFrom
            filters_state.deliveredAtTo = params.deliveredAtTo
            filters_state.additionalServiceStatusId = params.additionalServiceStatusId

            loadDataWithServices(1)
        }

        const resetFilters = () => {
            filters_state.additional_services = []
            filters_state.created_at = null
            filters_state.from = null
            filters_state.to = null
            filters_state.invoice_number = null
            filters_state.order_id = null
            filters_state.company_id = null
            filters_state.statusIds = []
            filters_state.isPickup = 0
            filters_state.isDelivery = 0
            filters_state.isBillingEnabled = false
            filters_state.takeDateFrom = null
            filters_state.takeDateTo = null
            filters_state.deliveredAtFrom = null
            filters_state.deliveredAtTo = null
            filters_state.additionalServiceStatusId = null

            loadDataWithServices(1)
        }

        const openEnterData = (item: DataWithServicesT) => {
            modals_state.invoice_id = item.meta.invoice_id
            modals_state.invoice_number = item.meta.invoice_number
            modals_state.additional_services = item.additional_service
            modals_state.is_on_enter_data = true
        }

        const closeEnterData = () => {
            modals_state.invoice_id = null
            modals_state.invoice_number = null
            modals_state.is_on_enter_data = false
        }

        const completeData = async (payload: CompleteDataWithServicesPayloadT[]) => {
            try {
                state.is_loading = true

                await invoices.enterDataWithServices(modals_state.invoice_id as number, payload)

                notifications.notify({
                    title: 'Данные успешно внесены',
                    type: 'success',
                })

                loadDataWithServices(state.pagination.current_page)

                closeEnterData()
            } catch (error) {
                notifications.notifyError({
                    error,
                    title: 'При внесении данных произошла ошбика',
                })
            } finally {
                state.is_loading = false
            }
        }

        const isCanComplete = (item: DataWithServicesT) => {
            return (
                item.additional_service_status === 'created' &&
                user.havePermission('order-logistics-info-complete-additional-services')
            )
        }

        const isCanDeleteAdditional = computed(() => {
            return user.havePermission('additional-service-value-delete')
        })

        const afterServiceDeleted = () => {
            modals_state.is_on_enter_data = false
            loadDataWithServices(state.pagination.current_page)
        }

        const onLimitChange = (limit: number) => {
            filters_state.limit = limit
            loadDataWithServices(state.pagination.current_page)
        }

        const saveExcel = (response: InvoicesDownloadExcelResponseT | null) => {
            if (!response) return

            const download_url = window.URL.createObjectURL(response)
            let file_name = 'Накладные & Заказы с доп. услугами.xlsx'

            saveFileByURL(download_url, file_name)
        }
        const exportExcel = async () => {
            try {
                state.is_loading = true

                const response = await invoices.exportExcelWithServices(getPayload())

                saveExcel(response)
            } catch (error) {
                notifications.notifyError({
                    error,
                    title: 'Ошибка скачивания Excel',
                })
            } finally {
                state.is_loading = false
            }
        }

        //

        const getAdditionalServices = computed(() => {
            return modals_state.additional_services
                ? modals_state.additional_services.filter((service) => service.isBillingEnabled)
                : []
        })

        const isCanDownloadExcel = computed(() => {
            return user.havePermission('order-logistic-info-index')
        })

        //

        return {
            DataWithServicesColumns,
            state,
            filters_state,
            modals_state,
            //
            loadDataWithServices,
            cutName,
            applyFilters,
            resetFilters,
            openEnterData,
            closeEnterData,
            completeData,
            isCanComplete,
            isCanDeleteAdditional,
            isCanDownloadExcel,
            afterServiceDeleted,
            onLimitChange,
            exportExcel,
            //
            getAdditionalServices,
            //
            WITH_SERVICES_STATUSES_COLORS,
            formatDateWithTimeFromBackend,
        }
    },
})
